/* eslint-disable jsx-a11y/no-autofocus -- Forward the existing native form focus contract to the shared control. */
import React from 'react';
import { SearchableDropdown, type SearchableDropdownOption } from '../common/SearchableDropdown';
import { cn } from '../design-system';
import './FormComponents.css';

interface FormFieldProps {
  label: React.ReactNode;
  required?: boolean;
  error?: string;
  hint?: string;
  children: React.ReactNode;
  className?: string;
  icon?: React.ReactNode;
  htmlFor?: string;
}

export const FormField: React.FC<FormFieldProps> = ({
  label,
  required = false,
  error,
  hint,
  children,
  className = '',
  icon,
  htmlFor
}) => {
  return (
    <div className={cn('flex flex-col', className)}>
      <div className="flex items-center space-x-2 mb-1">
        {icon && (
          <span className="crm-field-icon text-sm">
            {icon}
          </span>
        )}
        <label htmlFor={htmlFor} className="crm-field-label text-sm font-semibold">
          {label}{required && <span className="crm-field-required" aria-hidden="true"> *</span>}
        </label>
      </div>
      {/* Reserve a single line for hint so neighboring fields stay vertically aligned */}
      <div className="h-4 mb-1">
        {hint && !error && (
          <span className="crm-field-hint text-xs font-normal leading-none">
            {hint}
          </span>
        )}
      </div>
      <div className="relative">
        {children}
        {error && (
          <div className="crm-field-error flex items-center mt-2 text-xs" role="alert">
            <i className="fas fa-exclamation-circle mr-1"></i>
            {error}
          </div>
        )}
      </div>
    </div>
  );
};

interface ProfessionalInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  icon?: React.ReactNode;
  error?: boolean;
}

export const ProfessionalInput: React.FC<ProfessionalInputProps> = ({
  className = '',
  disabled,
  icon,
  error,
  ...props
}) => {
  return (
    <div className="relative">
      {icon && (
        <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
          {icon}
        </div>
      )}
      <input
        className={cn(
          'w-full rounded-lg border transition-all duration-200 text-sm',
          'crm-professional-control',
          'focus:outline-none focus:ring-2 focus:ring-indigo-500/40 focus:border-indigo-400',
          disabled 
            ? 'bg-gray-100 cursor-not-allowed text-gray-400 border-gray-300' 
            : 'bg-white text-gray-900 border-gray-300 hover:border-gray-400',
          error 
            ? 'border-red-500 ring-1 ring-red-500/30 focus:ring-red-500/40 focus:border-red-400'
            : '',
          icon ? 'pl-10 pr-4 py-3' : 'px-4 py-3',
          className
        )}
        disabled={disabled}
        aria-invalid={error || undefined}
        {...props}
      />
    </div>
  );
};

interface ProfessionalSelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  icon?: React.ReactNode;
  error?: boolean;
}

const optionText = (node: React.ReactNode): string => React.Children.toArray(node).map(child => React.isValidElement<{children?:React.ReactNode}>(child) ? optionText(child.props.children) : String(child)).join('');
const selectOptions = (children: React.ReactNode, groupDisabled = false): SearchableDropdownOption[] => React.Children.toArray(children).flatMap(child => {
  if (!React.isValidElement<{value?:string | number;label?:string;disabled?:boolean;children?:React.ReactNode}>(child)) return [];
  if (child.type === 'option') return [{value:String(child.props.value ?? optionText(child.props.children)), label:child.props.label ?? optionText(child.props.children), disabled:groupDisabled || child.props.disabled}];
  return selectOptions(child.props.children, groupDisabled || !!child.props.disabled);
});

/** Native-form compatibility; the existing global control owns the UI. */
export const ProfessionalSelect: React.FC<ProfessionalSelectProps> = ({className='', disabled, error, children, value, defaultValue, onChange, multiple, required, id, name, style, autoFocus, onBlur, onFocus, ...props}) => {
  const options = selectOptions(children);
  const normalize = (value: ProfessionalSelectProps['value']) => Array.isArray(value) ? value.map(String) : value == null ? undefined : String(value);
  const change = (next: string | number | (string | number)[] | null) => {
    const values = Array.isArray(next) ? next.map(String) : [String(next ?? '')];
    const target = {value:values[0] ?? '', name:name ?? '', selectedOptions:values.map(value=>({value,label:options.find(option=>option.value===value)?.label ?? value}))};
    onChange?.({target,currentTarget:target} as unknown as React.ChangeEvent<HTMLSelectElement>);
  };
  return <div className={cn('relative',className)} style={style}>
    <SearchableDropdown id={id} name={name} ariaLabel={props['aria-label']} ariaLabelledBy={props['aria-labelledby']} ariaDescribedBy={props['aria-describedby']} disabled={disabled} required={required} multiple={multiple} error={error} autoFocus={autoFocus} clearable={!!multiple || options.some(option=>option.value==='')}
      value={normalize(value)} defaultValue={normalize(defaultValue) ?? (multiple ? [] : options.find(option=>!option.disabled)?.value ?? '')} options={options} onChange={change}
      onBlur={event=>onBlur?.(event as unknown as React.FocusEvent<HTMLSelectElement>)} onFocus={event=>onFocus?.(event as unknown as React.FocusEvent<HTMLSelectElement>)} />
  </div>;
};

export default FormField;
